import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { googleAuthUrl } from "../lib/attribution";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resendStatus, setResendStatus] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false); // ← New loading state
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;
  if (!API_URL) throw new Error("REACT_APP_API_URL is not defined");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setShowResend(false);
    setResendStatus("");
    setIsLoggingIn(true); // Start loading

    try {
      await api.post("/api/auth/login", { email, password });
      navigate("/");
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError("Please verify your email first");
        setShowResend(true);
      } else {
        setError("Invalid email or password");
      }
    } finally {
      setIsLoggingIn(false); // Stop loading
    }
  };

  const resendVerification = async () => {
    setResendStatus("Sending...");
    try {
      await api.post("/api/auth/resend-verification", { email });
      setResendStatus("Verification email sent (if account exists)");
    } catch {
      setResendStatus("Failed to send email");
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = googleAuthUrl(API_URL);
  };

  return (
    <div className="page-shell flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold text-secondary mb-2">Welcome back</h1>
          <p className="text-textMuted">Log in to continue to DeCipher</p>
        </div>

        <div className="ui-card p-8">
          <button
            onClick={handleGoogleLogin}
           className="btn-secondary mb-6 w-full border-2 py-3"
          >
           <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg> 
            Continue with Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-textMuted">or</span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="ui-input"
              disabled={isLoggingIn}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="ui-input"
              disabled={isLoggingIn}
            />

            {error && <p className="text-center text-sm font-medium text-red-600">{error}</p>}

            {showResend && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={resendVerification}
                  className="text-sm font-medium text-primary hover:underline"
                  disabled={isLoggingIn}
                >
                  Resend verification email
                </button>
                {resendStatus && <p className="text-sm text-textMuted mt-2">{resendStatus}</p>}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoggingIn}
              className={`btn-primary w-full py-4 text-lg ${isLoggingIn ? "cursor-not-allowed bg-blue-300 hover:bg-blue-300" : ""}`}
            >
              {isLoggingIn ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </>
              ) : (
                "Log In"
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/forgot-password" className="text-sm font-medium text-primary hover:underline">
              Forgot password?
            </a>
          </div>

          <p className="mt-8 text-center text-textMuted">
            Don't have an account?{" "}
            <a href="/register" className="font-semibold text-primary hover:underline">
              Sign up
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}